import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';
import { forkJoin, Observable } from 'rxjs';
import { QuoteStatus } from '../models/quote.model';

export enum PlanningType {
    heatpump = 'HEAT_PUMP_PLANNER',
    photovoltaic = 'PHOTOVOLTAIC_PLANNER',
    boiler = 'BOILER_PLANNER',
}

export interface SalesforceQuoteResponse {
    totalCount: number;
    items: {
        title: string;
        documentId: string;
        revision: string;
        quickRef: string;
        status: 'New' | 'Quoted';
        createdDate: string;
        salesDocumentNumber: string;
        owner: {
            id: string;
            name: string;
        };
        salesAreaId: string;
        salesAreaName: string;
        externalApp: string;
        salesforceId: string;
        projectIdSF: string;
        planningId: string;
        planningType: 'HEAT_PUMP_PLANNER' | 'PHOTOVOLTAIC_PLANNER' | 'BOILER_PLANNER';
        variantDescription: string;
    }[];
}

export interface PlanningQuote {
    id: string;
    quickRef: string;
    status: QuoteStatus;
    customerPurchaseNumber: string;
    externalApp: string;
    salesforceOpportunityId: string;
    submissionNumber: string;
    offerNumber: string;
    planningId: string;
    planningType: PlanningType;
    variantDescription: string;
    migrationConfigitId?: string;
}

export interface PlanningProject {
    customerPurchaseNumber: string;
    externalApp: string;
    salesforceOpportunityId: string;
    submissionNumber: string;
    quotes: PlanningQuote[];
    variants?: PlanningVariants[];
}

export interface PlanningVariants {
    variantName: string;
    quotes: PlanningQuote[];
}

@Injectable({
    providedIn: 'root',
})
export class ProjectsService {
    constructor(private http: HttpClient) {}

    private getPlanningProjectsLegacy(salesforceId: string): Observable<PlanningQuote[]> {
        return this.http
            .get<SalesforceQuoteResponse>(`${environment.http.baseUrl}quote/planning-project/${salesforceId}`)
            .pipe(
                map((resp) => {
                    return resp.items.map((quote) => ({
                        id: quote.documentId,
                        quickRef: quote.quickRef,
                        status: <QuoteStatus>quote.status.toUpperCase(),
                        customerPurchaseNumber: quote.title,
                        externalApp: quote.externalApp,
                        salesforceOpportunityId: quote.salesforceId,
                        submissionNumber: quote.projectIdSF,
                        offerNumber: quote.salesDocumentNumber,
                        planningId: quote.planningId,
                        variantDescription: quote.variantDescription,
                        planningType: <PlanningType>quote.planningType,
                    }));
                })
            );
    }

    private getPlanningProjects(salesforceOpportunityId: string): Observable<PlanningQuote[]> {
        return forkJoin(
            ['quote-assist', 'pv-planner'].map((app) =>
                this.http.get<{ items: PlanningQuote[] }>(`${environment.http.quotation}quotations/${app}/quotes`, {
                    params: { salesforceOpportunityId },
                })
            )
        ).pipe(map(([resp1, resp2]) => [...resp1.items, ...resp2.items]));
    }

    public getPlanningProject(salesforceId: string): Observable<PlanningProject> {
        return forkJoin([this.getPlanningProjectsLegacy(salesforceId), this.getPlanningProjects(salesforceId)]).pipe(
            map(([legacy, projects]) => [...legacy, ...projects]),
            map((resp: PlanningQuote[]) => {
                const first = resp[0];
                const quotes = resp
                    .filter(
                        (item) =>
                            Object.values(PlanningType).includes(item.planningType) &&
                            !resp.some((i) => i.migrationConfigitId === item.id) // prefer converted quotes over legacy quotes
                    )
                    .sort((a, b) => a.planningType.localeCompare(b.planningType));
                return {
                    customerPurchaseNumber: first.customerPurchaseNumber,
                    externalApp: first.externalApp,
                    salesforceOpportunityId: first.salesforceOpportunityId,
                    submissionNumber: first.submissionNumber,
                    quotes,
                };
            })
        );
    }
}
